import React, { useState, useEffect, useContext } from 'react';
import {  DELETE_DOMAIN_PERMISSION_MESSAGE, DESCRIPTION, DOMAIN_NAME, NO_OF_CHANNEL, UPDATE_DOMAIN_PERMISSION_MESSAGE } from '../Utils/Constants';
import TableCell from 'Components/Common/components/TableCell';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu, DropdownMenuItem } from 'Pages/GMS/ISR/UI-Model/components/Common/DropdownMenu';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';

import { toast } from 'react-toastify';
import { TIndustry } from '../Types/domain.types';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { AppContext } from 'Context/AppContext';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type DomainTableProps = {
    partyId: string;
    searchTerm: string;
    showLoader: boolean;
    domains: TIndustry[]
    setDomains: React.Dispatch<React.SetStateAction<TIndustry[]>>
    setShowLoader: (value: boolean) => void;
    setTotalRecords: (value: number) => void;
    paginationParams: { pageNumber: number; pageSize: number };
    onDomainDeleted?: () => void;
    refreshTrigger?: number;
    onDomainsLoaded?: () => void;
    onEditDomain?: (domain: TIndustry, index: number) => void;
    onNavigateToChannel?: (domain: TIndustry) => void;
    deletePermission: any;
    updatePermission: any;
}

const DomainTable = ({ partyId, searchTerm, showLoader, domains, setDomains, setShowLoader, setTotalRecords,paginationParams,onDomainDeleted,refreshTrigger, onDomainsLoaded, onEditDomain,onNavigateToChannel,updatePermission, deletePermission }: DomainTableProps) => {
    const appContext = useContext(AppContext);
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [deleteConfirmation, setDeleteConfirmation] = useState<{
        isOpen: boolean;
        domainIndex: number | null;
        domainName: string;
        domainId: string | null;
    }>({
        isOpen: false,
        domainIndex: null,
        domainName: '',
        domainId: null
    });
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (partyId) {
            fetchDomains();
        }
    }, [partyId, paginationParams, searchTerm, sortBy, sortOrder, refreshTrigger]);

    const fetchDomains = async () => {
        setShowLoader(true);
        try {
            const domainResponse = await ServiceWrapper.doGet( 
                `${api_base_url}party/${partyId}/industry?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            ); 
            const response = domainResponse?.data;
            if (response && response.data) {
                setDomains(response.data);
                setTotalRecords(response.pagination.total);
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
            toast.error('Failed to fetch domains. Please try again.');
        } finally {
            setShowLoader(false);
            if (onDomainsLoaded) {
                onDomainsLoaded();
            }
        }
    };
    
    const renderSortIcon = (column: string) => {
        if (sortBy !== column) {
            return <i className="fas fa-sort tw-text-gray-400 tw-ml-1" />;
        }
        return sortOrder === 'asc' ? 
            <i className="fas fa-sort-up tw-text-gray-700 tw-ml-1" /> : 
            <i className="fas fa-sort-down tw-text-gray-700 tw-ml-1" />;
    };

    const handleColumnClick = (column: string) => {
        if (!showLoader) {
            if (sortBy === column) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            } else {
                setSortBy(column);
                setSortOrder('asc');
            }
        }
    };

    const handleEdit = (domain: TIndustry, index: number) => {
        if (!updatePermission) {
            toast.warn(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }

        if (onEditDomain) {
            onEditDomain(domain, index);
        }
    };

    const handleDomainClick = (domain: TIndustry) => {
        if (onNavigateToChannel) {
            onNavigateToChannel(domain);
        }
    };

    const handleDeleteClick = (domain: TIndustry, index: number) => {
        if (!deletePermission) {
            toast.warn(DELETE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }  
        setDeleteConfirmation({
            isOpen: true,
            domainIndex: index,
            domainName: domain.name,
            domainId: domain.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.domainId) return;
        setIsDeleting(true);
        try {            
            const deleteResponse = await ServiceWrapper.doGet( 
                `${api_base_url}industry/delete/${deleteConfirmation.domainId}`, 
                null, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success(response.message || 'Domain deleted successfully!');
                fetchDomains();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete domain');
                return;
            }
            
        } catch (error) {
            console.error('Error deleting domain:', error);
            toast.error('Failed to delete domain. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                domainIndex: null,
                domainName: '',
                domainId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            domainIndex: null,
            domainName: '',
            domainId: null
        });
    };

    const getDropdownMenuItems = (domain: TIndustry, index: number): DropdownMenuItem[] => [
        {
            id: `edit-${index}`,
            label: 'Edit',
            className: 'tw-text-[#172B4D]',
            onClick: () => handleEdit(domain, index),
            icon: (
                <EditIcon />
            )
        },
        {
            id: `delete-${index}`,
            label: 'Delete',
            onClick: () => handleDeleteClick(domain, index),
            className: 'tw-text-[#172B4D]',
            divider: true,
            icon: (
                <DeleteIcon />
            )
        }
    ];

    const renderTableContent = () => {
        if (showLoader) {
            return (
                <tr>
                    <td colSpan={5} className="tw-text-center tw-py-8">
                        <div className="tw-inline-flex tw-items-center">
                            <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading domains...
                        </div>
                    </td>
                </tr>
            );
        }

        if (domains.length === 0) {
            return (
                <tr>
                    <td colSpan={5} className="tw-text-center tw-py-8 tw-text-gray-500">
                        No domains found
                    </td>
                </tr>
            );
        }

        return domains.map((domain, index) => (
            <tr key={domain.id}>
                <TableCell 
                    id={`domain-name-${index}`} 
                    rowIndex={index} 
                    onClick={() => handleDomainClick(domain)}
                    style={{ cursor: 'pointer' }}
                >
                    {domain.name}
                </TableCell>
                <TableCell id={`description-${index}`} rowIndex={index}>{domain.description || '-'}</TableCell>
                <TableCell id={`no-of-channels-${index}`} rowIndex={index}>{domain.parent_channel_count}</TableCell>
                <TableCell id={`actions-${index}`} rowIndex={index}>
                    <DropdownMenu
                        items={getDropdownMenuItems(domain, index)}
                        trigger={
                            <button id={`overflow-btn-${index}`} className="tw-p-1 hover:tw-bg-gray-100 tw-rounded">
                                <OverFlowIcon />
                            </button>
                        }
                        position="right"
                    />
                </TableCell>
            </tr>
        ));
    };

    return (
        <>
            <div id="domain-table" className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2">
                <div id="table-scroll-container" className="table-container" style={{ width: '100%', height: 'auto', overflow: 'auto' }}>
                    <table id="domain-data-table" className="tw-bg-[#DFE1E6] tw-w-full tw-table-fixed">
                        <thead id="domain-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                            <tr>
                                <th
                                    id="domain-name-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '32%' }}
                                    onClick={() => handleColumnClick('name')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{DOMAIN_NAME}</span>
                                        {renderSortIcon('name')}
                                    </div>
                                </th>
                                <th 
                                    id="description-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '32%' }}
                                    onClick={() => handleColumnClick('description')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{DESCRIPTION}</span>
                                        {renderSortIcon('description')}
                                    </div>
                                </th>
                                <th 
                                    id="no-of-channel-header" 
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200" 
                                    style={{ width: '31%' }}
                                    onClick={() => handleColumnClick('parent_channel_count')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{NO_OF_CHANNEL}</span>
                                        {renderSortIcon('parent_channel_count')}
                                    </div>
                                </th>
                                <th id="actions-header" className="table-header-style tw-w-[5%]"></th>
                            </tr>
                        </thead>
                        <tbody id="domain-table-body" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]">
                            {renderTableContent()}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Domain"
                message={`Are you sure you want to delete the domain "${deleteConfirmation.domainName}"?`}
                secondaryMessage={'This action cannot be undone.'}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isLoading={isDeleting}
            />
        </>
    );
}

export default DomainTable;